import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, ArrowRightIcon, CheckCircle2Icon, StarIcon, TruckIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
interface HeroSectionProps {
  onRentNow?: () => void;
}
export function HeroSection({
  onRentNow
}: HeroSectionProps) {
  const benefits = [{
    icon: TruckIcon,
    text: 'Free Delivery'
  }, {
    icon: ShieldCheckIcon,
    text: 'Fully Sanitized'
  }, {
    icon: ClockIcon,
    text: '24/7 Support'
  }];
  return <section id="home" className="relative min-h-[90vh] flex items-center bg-[#0a1628] overflow-hidden pt-20" data-id="element-164">
      {/* Modern Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-165">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} data-id="element-166" />
        {/* Soft glowing gradients */}
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-brand-blue/10 blur-[120px]" data-id="element-167" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-brand-red/10 blur-[120px]" data-id="element-168" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 py-12 lg:py-20" data-id="element-169">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-170">
          {/* Left Content Column */}
          <div className="lg:col-span-7 flex flex-col justify-center text-center lg:text-left" data-id="element-171">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-172">
              {/* Top Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6 mx-auto lg:mx-0" data-id="element-173">
                <div className="flex gap-0.5" data-id="element-174">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" data-id="element-175" />)}
                </div>
                <span className="text-xs font-medium text-white/80 tracking-wide uppercase" data-id="element-176">
                  Top Rated in DMV Area
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.1] tracking-tight mb-6" data-id="element-177">
                Move With <br className="hidden lg:block" data-id="element-178" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60" data-id="element-179">
                  Ease & Comfort
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto lg:mx-0 leading-relaxed mb-10" data-id="element-180">
                Premium wheelchair and scooter rentals delivered directly to
                your doorstep, hotel, or cruise ship. Experience the freedom to
                explore with our reliable, fully sanitized equipment.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start mb-12" data-id="element-181">
                <a href="#reserve" className="w-full sm:w-auto px-8 py-4 bg-brand-red text-white rounded-xl font-bold text-lg hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(230,57,70,0.3)] hover:shadow-[0_0_30px_rgba(230,57,70,0.5)] hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-182">
                  Reserve Now
                  <ArrowRightIcon className="w-5 h-5" data-id="element-183" />
                </a>
                <a href="tel:+18003954459" className="w-full sm:w-auto px-8 py-4 bg-white/5 border border-white/20 text-white rounded-xl font-bold text-lg hover:bg-white hover:text-brand-navy transition-all flex items-center justify-center gap-2 backdrop-blur-sm" data-id="element-184">
                  <PhoneIcon className="w-5 h-5" data-id="element-185" />
                  (800) 395-4459
                </a>
              </div>

              {/* Benefits Row */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 sm:gap-8" data-id="element-186">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.4 + index * 0.1
              }} className="flex items-center gap-2" data-id="element-187">
                    <benefit.icon className="w-5 h-5 text-emerald-400" data-id="element-188" />
                    <span className="text-sm font-medium text-white/80" data-id="element-189">
                      {benefit.text}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>

          {/* Right Image/Product Column */}
          <div className="lg:col-span-5 relative mt-8 lg:mt-0" data-id="element-190">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            x: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            x: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative w-full max-w-md mx-auto lg:ml-auto lg:mr-0" data-id="element-191">
              {/* Decorative background shape */}
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-red/20 to-brand-blue/20 rounded-[2.5rem] transform rotate-3 scale-105 blur-lg" data-id="element-192" />

              {/* Main Product Card */}
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-[2rem] p-6 sm:p-8 shadow-2xl" data-id="element-193">
                {/* Floating Price Badge */}
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute -top-6 -right-4 sm:-right-6 bg-brand-red text-white px-5 py-3 rounded-2xl shadow-xl border border-white/10 z-20" data-id="element-194">
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-90 mb-0.5" data-id="element-195">
                    Starting at
                  </div>
                  <div className="flex items-baseline gap-1" data-id="element-196">
                    <span className="text-2xl font-black" data-id="element-197">$45</span>
                    <span className="text-sm font-medium opacity-90" data-id="element-198">/day</span>
                  </div>
                </motion.div>

                {/* Product Image */}
                <div className="relative h-64 sm:h-72 flex items-center justify-center mb-6" data-id="element-199">
                  <div className="absolute inset-0 bg-white/5 rounded-full blur-2xl" data-id="element-200" />
                  <img src="https://cdn.magicpatterns.com/uploads/vFPFQp8zQoU1RG9bZBkQpB/scooter-rfwu7jdi9ugdjjqqi1u1fo1vyfphtbhnxy7k3286bk-removebg-preview.png" alt="Heavy-Duty Mobility Scooter" className="w-full h-full object-contain drop-shadow-2xl relative z-10" data-id="element-201" />
                </div>

                {/* Product Info */}
                <div className="text-center" data-id="element-202">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-203">
                    Heavy-Duty Scooter
                  </h3>
                  <p className="text-white/60 text-sm mb-6" data-id="element-204">
                    3 or 4 wheel • All terrain • Comfortable seat
                  </p>

                  <button onClick={onRentNow} className="w-full py-3.5 bg-white text-brand-navy rounded-xl font-bold text-sm hover:bg-gray-100 transition-colors shadow-lg flex items-center justify-center gap-2 group" data-id="element-205">
                    Rent This Scooter
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-206" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Trust Strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-md border-t border-white/5 py-3 z-20" data-id="element-207">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2 text-center" data-id="element-208">
          <CheckCircle2Icon className="w-4 h-4 text-emerald-400 hidden sm:block" data-id="element-209" />
          <p className="text-white/80 text-sm font-medium tracking-wide" data-id="element-210">
            We are open <span className="text-white font-bold" data-id="element-211">24hrs</span> a
            day, <span className="text-white font-bold" data-id="element-212">7 days</span> a week,{' '}
            <span className="text-white font-bold" data-id="element-213">365 days</span> a year
          </p>
        </div>
      </div>
    </section>;
}